import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { SideBar } from 'components/side-bar/SideBar';
import { Outlet } from 'react-router-dom';
import { useAppDispatch } from 'store/storeHooks';
import { Header } from 'components/header/Header';
import styles from 'App.module.scss';
import { useMapClaimsToProfile } from 'hooks/hooks';
import { Spinner } from 'components/common/Spinner';
import { useAuth } from '@resmed/identity-spa-auth-react';
import { OAuthAgentAuthError, OAuthAgentCsrfError, OAuthAgentHttpError } from '@resmed/identity-spa-auth-js';
import { useTranslation } from 'react-i18next';
import { SessionManager } from 'components/common/SessionManager';
import { resetUserState } from 'store/user/userSlice';
import { logger } from 'utils/logger';
import { datadogRum } from '@datadog/browser-rum';
const isRecoverableClaimsError = (error) => error instanceof OAuthAgentHttpError &&
    !(error instanceof OAuthAgentAuthError) &&
    !(error instanceof OAuthAgentCsrfError) &&
    error.status === 400;
const CLAIMS_RECOVERY_ATTEMPTED_KEY = 'auth.claimsRecoveryAttempted';
const reportAuthErrorView = (error) => {
    datadogRum.addError(error, {
        errorMessage: error.message,
        errorStatus: error instanceof OAuthAgentHttpError ? error.status : undefined,
        errorCode: error instanceof OAuthAgentHttpError ? error.code : undefined,
        isRecoverableClaimsError: isRecoverableClaimsError(error)
    });
};
const App = () => {
    const { isLoading, isAuthenticated, error: authError, refresh } = useAuth();
    const dispatch = useAppDispatch();
    const wasAuthenticatedRef = useRef(isAuthenticated);
    const hasAttemptedRecoveryRef = useRef(false);
    const [isRecoveringAuth, setIsRecoveringAuth] = useState(false);
    useMapClaimsToProfile();
    const { t } = useTranslation('app');
    useEffect(() => {
        if (authError) {
            const isRecoverable = isRecoverableClaimsError(authError);
            logger.info('App: authError from useAuth hook', {
                error: authError,
                errorName: authError.name,
                errorMessage: authError.message,
                errorStatus: authError instanceof OAuthAgentHttpError ? authError.status : undefined,
                errorCode: authError instanceof OAuthAgentHttpError ? authError.code : undefined,
                isRecoverableClaimsError: isRecoverable
            });
        }
    }, [authError]);
    useEffect(() => {
        if (!authError || isRecoverableClaimsError(authError)) {
            return;
        }
        reportAuthErrorView(authError);
    }, [authError]);
    useEffect(() => {
        var _a, _b;
        if (!authError || hasAttemptedRecoveryRef.current || !isRecoverableClaimsError(authError)) {
            return;
        }
        hasAttemptedRecoveryRef.current = true;
        let alreadyAttemptedAcrossReload;
        try {
            alreadyAttemptedAcrossReload = ((_b = (_a = globalThis.sessionStorage) === null || _a === void 0 ? void 0 : _a.getItem) === null || _b === void 0 ? void 0 : _b.call(_a, CLAIMS_RECOVERY_ATTEMPTED_KEY)) === 'true';
        }
        catch (_c) {
            alreadyAttemptedAcrossReload = true;
        }
        if (alreadyAttemptedAcrossReload) {
            logger.warn('App: claims error persisted after a previous recovery reload, not retrying', {
                errorMessage: authError.message,
                errorStatus: authError instanceof OAuthAgentHttpError ? authError.status : undefined,
                errorCode: authError instanceof OAuthAgentHttpError ? authError.code : undefined
            });
            reportAuthErrorView(authError);
            return;
        }
        setIsRecoveringAuth(true);
        logger.info('App: attempting token refresh to recover from a recoverable claims error', {
            errorMessage: authError.message,
            errorStatus: authError instanceof OAuthAgentHttpError ? authError.status : undefined,
            errorCode: authError instanceof OAuthAgentHttpError ? authError.code : undefined
        });
        refresh()
            .then(() => {
            logger.info('App: token refresh succeeded, reloading to recover from claims error');
            try {
                if (!globalThis.sessionStorage) {
                    throw new Error('sessionStorage is unavailable');
                }
                globalThis.sessionStorage.setItem(CLAIMS_RECOVERY_ATTEMPTED_KEY, 'true');
            }
            catch (storageError) {
                logger.info('App: could not persist claims recovery guard; not reloading', { error: storageError });
                setIsRecoveringAuth(false);
                reportAuthErrorView(authError);
                return;
            }
            globalThis.location.reload();
        })
            .catch((refreshError) => {
            logger.info('App: failed to recover from authError via token refresh', {
                error: refreshError,
                errorMessage: refreshError instanceof Error ? refreshError.message : String(refreshError),
                errorStatus: refreshError instanceof OAuthAgentHttpError ? refreshError.status : undefined,
                errorCode: refreshError instanceof OAuthAgentHttpError ? refreshError.code : undefined
            });
            setIsRecoveringAuth(false);
            reportAuthErrorView(authError);
        });
    }, [authError, refresh]);
    useEffect(() => {
        var _a, _b;
        if (!authError && isAuthenticated) {
            try {
                (_b = (_a = globalThis.sessionStorage) === null || _a === void 0 ? void 0 : _a.removeItem) === null || _b === void 0 ? void 0 : _b.call(_a, CLAIMS_RECOVERY_ATTEMPTED_KEY);
            }
            catch (_c) {
                // Ignore storage errors (e.g. disabled or unavailable).
            }
            hasAttemptedRecoveryRef.current = false;
        }
    }, [authError, isAuthenticated]);
    useEffect(() => {
        if (wasAuthenticatedRef.current && !isAuthenticated) {
            dispatch(resetUserState());
        }
        wasAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated, dispatch]);
    if (isRecoveringAuth) {
        return _jsx(Spinner, {});
    }
    if (authError) {
        return _jsx(Typography, { children: t('authError') });
    }
    if (isLoading || !isAuthenticated) {
        return (_jsxs(_Fragment, { children: [_jsx(Spinner, {}), isAuthenticated && _jsx(SessionManager, {})] }));
    }
    return (_jsxs(Box, { component: "main", className: styles.container, children: [_jsx(Header, {}), _jsxs(Box, { className: styles.main, children: [_jsx(SideBar, {}), _jsx(Box, { className: styles.content, children: _jsx(Stack, { spacing: 3.5, children: _jsx(Outlet, {}) }) })] }), _jsx(SessionManager, {})] }));
};
export default App;
